import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { Clock, ChevronRight } from 'lucide-react';

const blogPosts = [
  {
    slug: 'competitor-monitoring-shopify-2025',
    title: 'The Complete Guide to Competitor Monitoring for Shopify in 2025',
    excerpt: 'Everything you need to know about tracking your competition effectively as a Shopify merchant.',
    category: 'Strategy',
    readTime: '8 min read',
    date: 'Jan 15, 2025',
    featured: true,
  },
  {
    slug: 'meta-ads-spy-guide',
    title: 'How to Spy on Competitor Meta Ads Without Getting Lost',
    excerpt: 'A practical guide to monitoring Facebook and Instagram ad campaigns effectively.',
    category: 'Meta Ads',
    readTime: '6 min read',
    date: 'Jan 12, 2025',
  },
  {
    slug: 'tiktok-ads-ecommerce',
    title: 'TikTok Ads for E-Commerce: What Top Brands Do Differently',
    excerpt: 'Analysis of the most effective TikTok ad strategies from leading e-commerce brands.',
    category: 'TikTok',
    readTime: '7 min read',
    date: 'Jan 10, 2025',
  },
  {
    slug: 'pricing-strategy-intelligence',
    title: 'Using Pricing Intelligence to Win in E-Commerce',
    excerpt: 'How smart Shopify merchants use competitor pricing data to make better decisions.',
    category: 'Pricing',
    readTime: '5 min read',
    date: 'Jan 8, 2025',
  },
  {
    slug: 'google-ads-competitor-analysis-2025',
    title: 'Google Ads Competitor Analysis: A Step-by-Step Framework',
    excerpt: 'Learn how to analyze competitor Google Ads campaigns and find opportunities.',
    category: 'Google Ads',
    readTime: '9 min read',
    date: 'Jan 5, 2025',
  },
  {
    slug: 'market-signals-shopify',
    title: 'Reading Market Signals: How to Detect Competitor Strategy Shifts',
    excerpt: 'Understanding the tech and operational signals that reveal where competitors are heading.',
    category: 'Intelligence',
    readTime: '6 min read',
    date: 'Jan 3, 2025',
  },
];

export default function BlogIndexPage() {
  const featured = blogPosts.find(p => p.featured);
  const rest = blogPosts.filter(p => !p.featured);

  return (
    <MarketingLayout>
      <SEO
        title="Blog — Insights & Intelligence"
        description="Strategies, analysis, and practical guides for e-commerce competitive intelligence from the RADAR team."
        canonical="/blog"
      />
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-30" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Blog</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-foreground mb-4">
              Insights & Intelligence
            </h1>
            <p className="text-base text-muted-foreground max-w-[520px]">
              Strategies, analysis, and practical guides for e-commerce competitive intelligence.
            </p>
          </AnimatedReveal>
        </Container>
      </Section>

      <Section dense>
        <Container>
          {featured && (
            <AnimatedReveal className="mb-12">
              <Link to={`/blog/${featured.slug}`}>
                <GlassPanel hover className="p-8 sm:p-10 group">
                  <div className="flex items-center gap-3 mb-4">
                    <SignalChip variant="lime">Featured</SignalChip>
                    <SignalChip>{featured.category}</SignalChip>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-3 group-hover:text-lime-400 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 max-w-2xl">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{featured.date}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.readTime}</span>
                  </div>
                </GlassPanel>
              </Link>
            </AnimatedReveal>
          )}

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {rest.map((post, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <Link to={`/blog/${post.slug}`}>
                  <GlassPanel hover className="p-6 h-full flex flex-col group">
                    <SignalChip className="self-start mb-4">{post.category}</SignalChip>
                    <h3 className="text-base font-display font-semibold text-foreground mb-2 group-hover:text-lime-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{post.date}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>
                    </div>
                  </GlassPanel>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
